import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, Trophy, Flame, Target, BookOpen, Brain, 
  Gamepad2, Calendar, Star, ChevronRight, Sparkles, Medal,
  BarChart3, Zap, Award, CheckCircle2
} from 'lucide-react';
import { motion } from 'framer-motion';

const PremiumStatsCard = () => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    practices: null,
    journal: null,
    points: null
  });
  
  const isGerman = i18n.language?.startsWith('de');

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };
    const baseUrl = process.env.REACT_APP_BACKEND_URL;

    try {
      // Fetch all stats in parallel
      const [practicesRes, journalRes, pointsRes] = await Promise.all([
        fetch(`${baseUrl}/api/practices/tracking/stats/progress-overview`, { headers }).catch(() => null),
        fetch(`${baseUrl}/api/journal/stats/overview`, { headers }).catch(() => null),
        fetch(`${baseUrl}/api/points/summary`, { headers }).catch(() => null)
      ]);

      const practicesData = practicesRes?.ok ? await practicesRes.json() : null;
      const journalData = journalRes?.ok ? await journalRes.json() : null;
      const pointsData = pointsRes?.ok ? await pointsRes.json() : null;

      setStats({
        practices: practicesData,
        journal: journalData,
        points: pointsData
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-2 border-[#006039]/20">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#006039] border-t-transparent" />
            <span className="text-muted-foreground">
              {isGerman ? 'Lade Statistiken...' : 'Loading stats...'}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate combined stats
  const practiceStreak = stats.practices?.current_streak || 0;
  const journalStreak = stats.journal?.current_streak || 0;
  const totalPoints = stats.points?.total_points || 0;
  const gamesPlayed = stats.points?.games_played || 0;
  const achievementsUnlocked = stats.points?.achievements_unlocked || 0;
  const weeklyActivity = stats.practices?.weekly_activity || [];
  const totalSessions = stats.practices?.total_sessions || 0;
  const journalEntries = stats.journal?.total_entries || 0;
  
  // Calculate week completion percentage
  const weekDaysCompleted = weeklyActivity.filter(d => d.completed > 0).length;
  const weekProgress = Math.round((weekDaysCompleted / 7) * 100);

  // Check if user has any activity
  const hasActivity = totalSessions > 0 || journalEntries > 0 || gamesPlayed > 0;

  if (!hasActivity) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-[#006039]/5 via-emerald-500/5 to-teal-500/5 border-2 border-dashed border-[#006039]/30">
          <CardContent className="py-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#006039]/10 mb-4">
              <Sparkles className="h-8 w-8 text-[#006039]" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {isGerman ? 'Starte Deine Reise!' : 'Start Your Journey!'}
            </h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              {isGerman 
                ? 'Beginne mit einer Praktik, einem Tagebucheintrag oder einem Fokus-Spiel und verfolge hier Deinen Fortschritt.'
                : 'Begin with a practice, journal entry, or focus game and track your progress here.'}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/practices">
                <Badge variant="outline" className="px-4 py-2 cursor-pointer hover:bg-[#006039]/10 transition-colors">
                  <Target className="h-4 w-4 mr-2" />
                  {isGerman ? 'Praktiken' : 'Practices'}
                </Badge>
              </Link>
              <Link to="/journal">
                <Badge variant="outline" className="px-4 py-2 cursor-pointer hover:bg-[#006039]/10 transition-colors">
                  <BookOpen className="h-4 w-4 mr-2" />
                  {isGerman ? 'Tagebuch' : 'Journal'}
                </Badge>
              </Link>
              <Link to="/focus-games">
                <Badge variant="outline" className="px-4 py-2 cursor-pointer hover:bg-[#006039]/10 transition-colors">
                  <Gamepad2 className="h-4 w-4 mr-2" />
                  {isGerman ? 'Spiele' : 'Games'}
                </Badge>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#006039] to-emerald-600">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-xl font-serif font-bold">
            {isGerman ? 'Dein Fortschritt' : 'Your Progress'}
          </h2>
        </div>
        <Link to="/progress" className="text-sm text-[#006039] hover:underline flex items-center gap-1">
          {isGerman ? 'Details' : 'Details'}
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Main Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Combined Streak */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border-orange-200/50 dark:border-orange-800/30 h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/20">
                  <Flame className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {Math.max(practiceStreak, journalStreak)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isGerman ? 'Tage in Folge' : 'Day Streak'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Total Points */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ delay: 0.15 }}
        >
          <Card className="bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border-yellow-200/50 dark:border-yellow-800/30 h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-500 shadow-lg shadow-yellow-500/20">
                  <Star className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{totalPoints}</p>
                  <p className="text-xs text-muted-foreground">
                    {isGerman ? 'Punkte' : 'Points'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Achievements */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-gradient-to-br from-purple-500/10 to-violet-500/5 border-purple-200/50 dark:border-purple-800/30 h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 shadow-lg shadow-purple-500/20">
                  <Trophy className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{achievementsUnlocked}</p>
                  <p className="text-xs text-muted-foreground">
                    {isGerman ? 'Erfolge' : 'Achievements'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Week Progress */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ delay: 0.25 }}
        >
          <Card className="bg-gradient-to-br from-[#006039]/10 to-emerald-500/5 border-[#006039]/20 dark:border-emerald-800/30 h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#006039] to-emerald-500 shadow-lg shadow-emerald-500/20">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{weekDaysCompleted}/7</p>
                  <p className="text-xs text-muted-foreground">
                    {isGerman ? 'Diese Woche' : 'This Week'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Weekly Activity Chart */}
      {weeklyActivity.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-[#006039]" />
                  {isGerman ? 'Wochenaktivität' : 'Weekly Activity'}
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {weekProgress}% {isGerman ? 'abgeschlossen' : 'complete'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="flex items-end justify-between gap-2 h-24">
                {weeklyActivity.map((day, index) => {
                  const maxCompleted = Math.max(...weeklyActivity.map(d => d.completed), 1);
                  const heightPercent = day.completed > 0 ? Math.max((day.completed / maxCompleted) * 100, 20) : 8;
                  const isToday = index === new Date().getDay() - 1 || (index === 6 && new Date().getDay() === 0);
                  
                  return (
                    <div key={day.day} className="flex-1 flex flex-col items-center gap-2">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${heightPercent}%` }}
                        transition={{ delay: 0.4 + index * 0.05, duration: 0.5, ease: "easeOut" }}
                        className={`w-full max-w-[40px] rounded-t-lg transition-colors ${
                          day.completed > 0 
                            ? 'bg-gradient-to-t from-[#006039] to-emerald-400' 
                            : 'bg-slate-200 dark:bg-slate-700'
                        } ${isToday ? 'ring-2 ring-[#006039]/50 ring-offset-2' : ''}`}
                      />
                      <span className={`text-xs font-medium ${
                        isToday ? 'text-[#006039]' : 'text-muted-foreground'
                      }`}>
                        {day.day}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Activity Summary Row */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-3 gap-3"
      >
        {/* Practices */}
        <Link to="/practices">
          <Card className="bg-white/60 dark:bg-slate-900/60 hover:bg-white/90 dark:hover:bg-slate-900/90 transition-all cursor-pointer border hover:border-[#006039]/30 h-full">
            <CardContent className="p-3 text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-2">
                <Target className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-xl font-bold">{totalSessions}</p>
              <p className="text-xs text-muted-foreground">
                {isGerman ? 'Sessions' : 'Sessions'}
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* Journal */}
        <Link to="/journal">
          <Card className="bg-white/60 dark:bg-slate-900/60 hover:bg-white/90 dark:hover:bg-slate-900/90 transition-all cursor-pointer border hover:border-[#006039]/30 h-full">
            <CardContent className="p-3 text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-2">
                <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-xl font-bold">{journalEntries}</p>
              <p className="text-xs text-muted-foreground">
                {isGerman ? 'Einträge' : 'Entries'}
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* Games */}
        <Link to="/focus-games">
          <Card className="bg-white/60 dark:bg-slate-900/60 hover:bg-white/90 dark:hover:bg-slate-900/90 transition-all cursor-pointer border hover:border-[#006039]/30 h-full">
            <CardContent className="p-3 text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 mb-2">
                <Gamepad2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-xl font-bold">{gamesPlayed}</p>
              <p className="text-xs text-muted-foreground">
                {isGerman ? 'Spiele' : 'Games'}
              </p>
            </CardContent>
          </Card>
        </Link>
      </motion.div>

      {/* Best Records */}
      {(stats.practices?.longest_streak > 0 || stats.journal?.longest_streak > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 border-amber-200/50 dark:border-amber-800/30">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Medal className="h-5 w-5 text-amber-500" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {isGerman ? 'Persönliche Bestleistungen' : 'Personal Bests'}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  {stats.practices?.longest_streak > 0 && (
                    <div className="flex items-center gap-1.5">
                      <Target className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                        {stats.practices.longest_streak} {isGerman ? 'Tage' : 'days'}
                      </span>
                    </div>
                  )}
                  {stats.journal?.longest_streak > 0 && (
                    <div className="flex items-center gap-1.5">
                      <BookOpen className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                        {stats.journal.longest_streak} {isGerman ? 'Tage' : 'days'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PremiumStatsCard;
